import type { ReactNode } from 'react';
import { cn } from '../cn';

interface CaptureApertureProps {
  /**
   * Without children: renders the K aperture mark at `size` px.
   * With children: renders them inside the aperture corner brackets —
   * the treatment for the current capture or the selected visual evidence.
   */
  children?: ReactNode;
  size?: number;
  /** Bracket colour follows the state: active/recording = Harvest Flame. */
  state?: 'idle' | 'active' | 'recording';
  /** Mark sits on a dark surface (HUD, rail). */
  onDark?: boolean;
  /** Show the K glyph inside the standalone mark. */
  glyph?: boolean;
  label?: string;
  className?: string;
}

function bracketColor(state: 'idle' | 'active' | 'recording', onDark: boolean | undefined) {
  if (state !== 'idle') return '#FA5D00';
  return onDark ? 'rgba(253,246,238,0.66)' : '#615F5C';
}

/**
 * The Kapture aperture — four viewfinder reticles, optionally framing the
 * K glyph or a piece of visual evidence. A system signature reserved for
 * the wordmark, the active capture control, selected evidence framing, and
 * high-value record identifiers; not a decorative repeat.
 */
export function CaptureAperture({
  children,
  size = 40,
  state = 'idle',
  onDark,
  glyph = true,
  label,
  className,
}: CaptureApertureProps) {
  const stroke = bracketColor(state, onDark);

  if (children !== undefined) {
    return (
      <span
        className={cn('relative block', className)}
        role={label ? 'img' : undefined}
        aria-label={label}
      >
        {children}
        <span aria-hidden className="pointer-events-none absolute inset-0">
          {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
            <span
              key={corner}
              className={cn(
                'absolute h-3 w-3 border-[2px]',
                corner === 'tl' && 'left-1 top-1 rounded-tl-sm border-b-0 border-r-0',
                corner === 'tr' && 'right-1 top-1 rounded-tr-sm border-b-0 border-l-0',
                corner === 'bl' && 'bottom-1 left-1 rounded-bl-sm border-r-0 border-t-0',
                corner === 'br' && 'bottom-1 right-1 rounded-br-sm border-l-0 border-t-0',
              )}
              style={{ borderColor: stroke }}
            />
          ))}
          {state === 'recording' && (
            <span className="absolute right-2 top-2 flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5">
              <span className="bg-brand motion-safe:animate-kap-pulse h-1.5 w-1.5 rounded-full" />
              <span className="font-mono text-[9px] font-medium uppercase tracking-[0.1em] text-white">
                Rec
              </span>
            </span>
          )}
        </span>
      </span>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('shrink-0', className)}
    >
      <path d="M10 16V10H16" stroke={stroke} strokeWidth={3} strokeLinecap="round" />
      <path d="M38 16V10H32" stroke={stroke} strokeWidth={3} strokeLinecap="round" />
      <path d="M10 32V38H16" stroke={stroke} strokeWidth={3} strokeLinecap="round" />
      <path d="M38 32V38H32" stroke={stroke} strokeWidth={3} strokeLinecap="round" />
      {glyph && (
        <path
          d="M18 15V33M18 24L28 15M22 22L30 33"
          stroke={state === 'idle' ? stroke : '#FA5D00'}
          strokeWidth={3.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {state === 'recording' && (
        <circle cx="24" cy="24" r="4" fill="#FA5D00" className="motion-safe:animate-kap-pulse" />
      )}
    </svg>
  );
}
